import React from 'react';
import styled from 'styled-components';
import Arrow, { ArrowWrapper } from '../Arrow';
import { WorkViewerInfoTextArea } from '../headless/textarea/TextAreaBody';
import { getAlertMessage } from '../../constants/alertMessages';
import {
  TEXTBLOCK_LINK_LABEL_MAX_LENGTH,
  TEXTBLOCK_LINK_URL_MAX_LENGTH,
} from '../../constants/limits';
import { StyledDivComponent, StyledTextAreaComponent } from '../../dto/StyleCompRepository';
import { AlertPosition, AlertType } from '../../enum/EnumRepository';
import { useEditMode } from '../../hooks/useEditMode';
import { useStandardAlertStore } from '../../store/portal/alertStore';
import { openNewTab } from '../../util/openNewTab';
import { parseSupportedTextBlockLinkUrl } from '../../util/textBlockLinkUrl';
import MoleculeTextAreaDiv from './MoleculeTextAreaDiv';

interface MoleculeTextBlockLinkProps {
  label: string;
  url: string;
  handleChangeLabel: (e: React.ChangeEvent<HTMLTextAreaElement>) => void;
  handleChangeUrl: (e: React.ChangeEvent<HTMLTextAreaElement>) => void;
  StyledLabel: StyledDivComponent;
  StyledLabelTextArea: StyledTextAreaComponent;
}

const MoleculeTextBlockLink: React.FC<MoleculeTextBlockLinkProps> = ({
  label,
  url,
  handleChangeLabel,
  handleChangeUrl,
  StyledLabel,
  StyledLabelTextArea,
}) => {
  const { isEditMode } = useEditMode();
  const { enqueueAlert } = useStandardAlertStore();
  const displayLabel = label.trim() || url.trim();
  const parsedLink = parseSupportedTextBlockLinkUrl(url);

  const handleOpen = (e: React.MouseEvent) => {
    e.preventDefault();
    if (!parsedLink) {
      enqueueAlert({
        type: AlertType.ALERT,
        position: AlertPosition.TOP,
        content: getAlertMessage('INVALID_TEXT_BLOCK_LINK_URL_FORMAT'),
      });
      return;
    }

    enqueueAlert({
      type: AlertType.CONFIRM,
      position: AlertPosition.TOP,
      content: getAlertMessage('CONFIRM_MOVE_TO_TEXT_BLOCK_LINK'),
      callBack: () => openNewTab(parsedLink.url.href),
    });
  };

  if (isEditMode) {
    return (
      <LinkEditWrapper>
        <MoleculeTextAreaDiv
          value={label}
          placeholder="Label"
          handleChange={handleChangeLabel}
          textareaStyle={StyledLabelTextArea}
          StyledDiv={StyledLabel}
          maxLength={TEXTBLOCK_LINK_LABEL_MAX_LENGTH}
          maxRows={100}
        />
        <MoleculeTextAreaDiv
          value={url}
          placeholder="https://drive.google.com/file/d/..."
          handleChange={handleChangeUrl}
          textareaStyle={LinkUrlTextArea}
          StyledDiv={LinkUrl}
          maxLength={TEXTBLOCK_LINK_URL_MAX_LENGTH}
          maxRows={100}
        />
      </LinkEditWrapper>
    );
  }

  if (!displayLabel) return null;

  if (!parsedLink) {
    return (
      <LinkView $isClickable={false}>
        <StyledLabel>{displayLabel}</StyledLabel>
      </LinkView>
    );
  }

  return (
    <LinkView $isClickable={true}>
      <StyledLabel
        as="a"
        href={parsedLink.url.href}
        target="_blank"
        rel="noopener noreferrer"
        onClick={handleOpen}
      >
        {displayLabel}
      </StyledLabel>
      <HoverArrow>
        <ArrowWrapper>
          <Arrow />
        </ArrowWrapper>
      </HoverArrow>
    </LinkView>
  );
};

export default MoleculeTextBlockLink;

const LinkUrl = styled.div`
  width: 100%;
  padding: 4px 0;
  color: ${({ theme }) => theme.colors.color_Gray_04};
  ${({ theme }) => theme.typography.Body_04};
`;

const LinkUrlTextArea = styled(WorkViewerInfoTextArea)`
  margin-top: 14px;
`;

const HoverArrow = styled.span`
  flex-shrink: 0;
  margin-left: 4px;
  opacity: 0;
  pointer-events: none;
  transition: opacity 0.2s ease;
`;

const LinkView = styled.div<{ $isClickable: boolean }>`
  display: inline-flex;
  align-items: center;
  width: fit-content;
  max-width: 100%;
  text-decoration: underline;
  text-underline-offset: 3px;
  cursor: ${({ $isClickable }) => ($isClickable ? 'pointer' : 'default')};

  > :first-child {
    width: auto;
    min-width: 0;
    max-width: 100%;
    border-bottom: none;
    overflow-wrap: anywhere;
  }

  &:hover ${HoverArrow} {
    opacity: ${({ $isClickable }) => ($isClickable ? 1 : 0)};
  }
`;

const LinkEditWrapper = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 4px;
`;
